import React from 'react';
import { BsSendCheckFill, BsSendExclamationFill, BsSendXFill } from 'react-icons/bs';
import { Tooltip } from 'rizzui';
import { Tutor } from '../../type/entities/tutor-type';

interface AgreementStatusIconProps {
  tutor: Tutor;
  onClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const AgreementStatusIcon: React.FC<AgreementStatusIconProps> = ({
  tutor,
  onClick,
  size = 'md',
  className = ''
}) => {
  const parseDate = (value: any): Date | null => {
    if (!value) return null;
    if (typeof value === 'string') return new Date(value);
    if (typeof value === 'object' && typeof value.date === 'string') return new Date(value.date);
    return null;
  };

  const isValidSignature = (signedAt: any) => {
    const date = parseDate(signedAt);
    if (!date) return false;
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return date > oneYearAgo;
  };

  // Determinar el estado del convenio
  const getAgreementStatus = () => {
    if (Array.isArray(tutor.providingCorporations) && tutor.providingCorporations.length > 0) {
      const requiringSignature = tutor.providingCorporations.filter((pc: any) => !!pc?.collaborationSignature);
      const total = requiringSignature.length;

      if (total === 0) {
        return {
          type: 'not_required',
          icon: BsSendCheckFill,
          color: 'text-green-500',
          bgColor: 'bg-green-50 hover:bg-green-100',
          borderColor: 'border-green-200',
          tooltip: 'No hay impartidoras que requieran firma de colaboración'
        };
      }

      const validSigned = requiringSignature.filter((pc: any) => pc?.hasAgreementSigned && isValidSignature(pc?.agreementSignedAt)).length;

      if (validSigned === total) {
        return {
          type: 'signed',
          icon: BsSendCheckFill,
          color: 'text-green-500',
          bgColor: 'bg-green-50 hover:bg-green-100',
          borderColor: 'border-green-200',
          tooltip: 'Convenios firmados y vigentes en todas las impartidoras'
        };
      }

      if (validSigned > 0) {
        return {
          type: 'partial',
          icon: BsSendExclamationFill,
          color: 'text-orange-500',
          bgColor: 'bg-orange-50 hover:bg-orange-100',
          borderColor: 'border-orange-200',
          tooltip: 'Hay impartidoras con convenio pendiente o caducado'
        };
      }

      return {
        type: 'not_signed',
        icon: BsSendXFill,
        color: 'text-red-500',
        bgColor: 'bg-red-50 hover:bg-red-100',
        borderColor: 'border-red-200',
        tooltip: 'No hay convenios vigentes - Clic para gestionar'
      };
    }

    if (typeof tutor.hasAgreementSigned !== 'boolean') {
      return {
        type: 'unknown',
        icon: BsSendExclamationFill,
        color: 'text-gray-500',
        bgColor: 'bg-gray-50 hover:bg-gray-100',
        borderColor: 'border-gray-200',
        tooltip: 'Estado por impartidora no cargado - Clic para gestionar'
      };
    }

    if (!tutor.hasAgreementSigned) {
      return {
        type: 'not_signed',
        icon: BsSendXFill,
        color: 'text-red-500',
        bgColor: 'bg-red-50 hover:bg-red-100',
        borderColor: 'border-red-200',
        tooltip: 'Convenio no firmado - Clic para gestionar'
      };
    }

    if (!isValidSignature(tutor.agreementSignedAt)) {
      return {
        type: 'expired',
        icon: BsSendExclamationFill,
        color: 'text-orange-500',
        bgColor: 'bg-orange-50 hover:bg-orange-100',
        borderColor: 'border-orange-200',
        tooltip: 'Convenio firmado hace más de 1 año - Clic para gestionar'
      };
    }

    return {
      type: 'signed',
      icon: BsSendCheckFill,
      color: 'text-green-500',
      bgColor: 'bg-green-50 hover:bg-green-100',
      borderColor: 'border-green-200',
      tooltip: 'Convenio firmado y vigente'
    };
  };

  const status = getAgreementStatus();
  const IconComponent = status.icon;

  // Tamaños de iconos
  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  // Tamaños de contenedor
  const containerSizes = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5'
  };

  const isClickable = !!onClick;

  return (
    <Tooltip
      content={status.tooltip}
      placement="top"
      color="invert"
      size="sm"
    >
      <div
        className={`
          inline-flex items-center justify-center rounded-full border transition-all duration-200
          ${status.bgColor} ${status.borderColor} ${containerSizes[size]}
          ${isClickable ? 'cursor-pointer hover:shadow-sm' : 'cursor-default'}
          ${className}
        `}
        onClick={isClickable ? onClick : undefined}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={isClickable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        } : undefined}
      >
        <IconComponent
          className={`${status.color} ${iconSizes[size]}`}
          aria-hidden="true"
        />
      </div>
    </Tooltip>
  );
};

export default AgreementStatusIcon;
